import { useEffect, useState } from "react";
import { useWhatsApp } from "@/components/feature/useWhatsApp";

const SNOOZE_KEY = "vv-wa-snooze-until";
const SNOOZE_MS = 3 * 24 * 60 * 60 * 1000;

function snoozed(): boolean {
  try {
    return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now();
  } catch {
    return false;
  }
}

/**
 * Al abrir la app, invita a activar los avisos por WhatsApp (recordatorios de
 * reuniones y eventos). Se muestra solo si el negocio ya configuró su número y
 * la persona aún no activó los avisos.
 */
export default function WhatsAppPrompt() {
  const { status, waiting, error, activate } = useWhatsApp();
  const [hidden, setHidden] = useState<boolean>(() => snoozed());
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (waiting === false && status?.optedIn && !hidden) {
      setDone(true);
      const t = window.setTimeout(() => setHidden(true), 3500);
      return () => window.clearTimeout(t);
    }
  }, [waiting, status?.optedIn, hidden]);

  if (hidden || !status || !status.number) return null;
  if (status.optedIn && !done) return null;

  const later = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS));
    } catch {
      /* sin almacenamiento: solo se oculta esta vez */
    }
    setHidden(true);
  };

  return (
    <div
      className="fixed z-40 left-3 right-3 bottom-[calc(5.5rem+env(safe-area-inset-bottom))] lg:left-auto lg:right-6 lg:bottom-6 lg:w-96 bg-white border border-emerald-200 rounded-2xl shadow-xl p-4"
      role="dialog"
      aria-label="Activar avisos por WhatsApp"
    >
      {done ? (
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center text-xl">
            <i className="ri-check-double-line"></i>
          </span>
          <div className="text-sm font-semibold text-slate-900">¡Listo! Tus avisos por WhatsApp están activados.</div>
        </div>
      ) : (
        <>
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-full bg-[#20A464] text-white flex items-center justify-center text-xl flex-shrink-0">
              <i className="ri-whatsapp-line"></i>
            </span>
            <div className="flex-1">
              <div className="font-heading font-bold text-slate-900 text-sm">Recibe tus recordatorios por WhatsApp</div>
              <p className="text-xs text-slate-600 mt-0.5">Te avisamos antes de tus reuniones y eventos para que no te pierdas ninguno. Puedes dejar de recibirlos cuando quieras.</p>
            </div>
            <button onClick={later} aria-label="Ahora no" className="w-7 h-7 rounded-full text-slate-400 hover:bg-slate-100 cursor-pointer">
              <i className="ri-close-line"></i>
            </button>
          </div>
          {error ? <p className="mt-2 text-xs text-rose-600">{error}</p> : null}
          <div className="mt-3 flex items-center gap-2">
            <button onClick={activate} disabled={waiting} className="flex-1 px-4 py-2.5 rounded-lg bg-[#20A464] hover:bg-[#1b8f57] text-white text-sm font-semibold disabled:opacity-70 cursor-pointer whitespace-nowrap">
              {waiting ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-1.5"></i>Esperando tu mensaje…
                </>
              ) : (
                <>
                  <i className="ri-whatsapp-line mr-1.5"></i>Activar en WhatsApp
                </>
              )}
            </button>
            <button onClick={later} className="px-3 py-2.5 text-xs font-semibold text-slate-500 hover:text-slate-700 cursor-pointer whitespace-nowrap">
              Ahora no
            </button>
          </div>
          {waiting ? <p className="mt-2 text-[11px] text-slate-500">Se abrió WhatsApp con un mensaje listo: solo envíalo y vuelve aquí.</p> : null}
        </>
      )}
    </div>
  );
}
